import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel } from "@/components/ui-bits";
import DataIOToolbar from "@/components/DataIOToolbar";

const empty = { name: "", contact: "", email: "", phone: "", address: "" };

export default function ContactList({ title, eyebrow, resource, testPrefix }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => setRows((await api.get(`/${resource}`)).data);
  useEffect(() => {
    load();
    // Re-fetch only when the resource path changes; `load` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  const startNew = () => {
    setForm(empty);
    setEditId(null);
    setOpen(true);
  };
  const startEdit = (r) => {
    setForm({
      name: r.name,
      contact: r.contact || "",
      email: r.email || "",
      phone: r.phone || "",
      address: r.address || "",
    });
    setEditId(r.id);
    setOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.patch(`/${resource}/${editId}`, form);
      else await api.post(`/${resource}`, form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch {
      toast.error("Failed");
    }
  };
  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/${resource}/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader title={title} eyebrow={eyebrow}>
        <DataIOToolbar resource={resource} label={title} onImported={load} />
        <Button
          className="h-12 px-5 gap-2"
          onClick={startNew}
          data-testid={`new-${testPrefix}-button`}
        >
          <Plus className="h-5 w-5" /> New {testPrefix}
        </Button>
      </PageHeader>
      <Panel title={`${title} (${rows.length})`}>
        {rows.length === 0 ? (
          <div className="py-10 text-center text-stone-500">No records yet.</div>
        ) : (
          <table className="w-full" data-testid={`${testPrefix}-table`}>
            <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
              <tr>
                <th className="py-3">Name</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Address</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border h-16">
                  <td className="font-medium">{r.name}</td>
                  <td>{r.contact}</td>
                  <td className="text-stone-600">{r.email}</td>
                  <td>{r.phone}</td>
                  <td className="text-stone-600 text-sm max-w-xs truncate">{r.address}</td>
                  <td className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(r)} data-testid={`edit-${testPrefix}-${r.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-${testPrefix}-${r.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testPrefix}-dialog`}>
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit" : "New"} {testPrefix}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Name</Label><Input className="h-12 mt-1" required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid={`${testPrefix}-name`} /></div>
            <div><Label>Contact person</Label><Input className="h-12 mt-1" value={form.contact} onChange={(e)=>setForm({...form, contact: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input className="h-12 mt-1" type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} /></div>
              <div><Label>Phone</Label><Input className="h-12 mt-1" value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} /></div>
            </div>
            <div><Label>Address</Label><Textarea value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} /></div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" className="h-12 px-6" data-testid={`${testPrefix}-submit`}>Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
